import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  styled,
} from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import { CURRENCY } from '../config/currency';

interface Portion {
  id: number;
  portionName: string;
  priceModifier: number;
  isDefault: boolean;
  displayOrder: number;
  effectivePrice: number;
  displayPrice: string;
}

interface PortionSelectorProps {
  portions: Portion[];
  selectedPortionId?: number;
  onChange: (portionId: number) => void;
  basePrice: number;
  inline?: boolean; // For compact display in menu items
}

const StyledRadio = styled(Radio)(({ theme }) => ({
  padding: 4,
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}));

const PriceModifierChip = styled(Chip)<{ modifier: number }>(({ theme, modifier }) => ({
  height: 20,
  fontSize: '0.75rem',
  marginLeft: theme.spacing(1),
  backgroundColor: modifier > 0 
    ? theme.palette.success.light 
    : modifier < 0 
      ? theme.palette.error.light 
      : theme.palette.grey[200],
  color: modifier > 0 
    ? theme.palette.success.dark 
    : modifier < 0 
      ? theme.palette.error.dark 
      : theme.palette.text.secondary,
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

const PortionSelector: React.FC<PortionSelectorProps> = ({
  portions,
  selectedPortionId,
  onChange,
  basePrice,
  inline = false,
}) => {
  const { theme } = useTheme();
  // Filter out any null or invalid portions first
  const validPortions = portions.filter(p => p && p.portionName);
  
  // Initialize with a valid default value
  const getDefaultPortionId = () => {
    if (selectedPortionId) return selectedPortionId;
    const defaultPortion = validPortions.find(p => p.isDefault) || validPortions[0];
    return defaultPortion?.id || 0;
  };
  
  const [selected, setSelected] = useState<number>(getDefaultPortionId());

  useEffect(() => {
    // If no portion is selected, select the default one
    if (!selected && validPortions.length > 0) {
      const defaultPortion = validPortions.find(p => p.isDefault) || validPortions[0];
      setSelected(defaultPortion.id);
      onChange(defaultPortion.id);
    }
  }, [validPortions, selected, onChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const portionId = parseInt(event.target.value);
    setSelected(portionId);
    onChange(portionId);
  };

  if (inline && validPortions.length <= 1) {
    // Don't show selector if there's only one portion in inline mode
    return null;
  }

  const formatPriceModifier = (modifier: number) => {
    if (modifier === 0) return null;
    return modifier > 0 ? `+${CURRENCY.symbol}${modifier.toFixed(2)}` : `-${CURRENCY.symbol}${Math.abs(modifier).toFixed(2)}`;
  };

  if (inline) {
    // Compact display for menu items - background now handled by parent
    return (
      <Box sx={{ 
        width: '100%'
      }}>
        <RadioGroup
          row
          value={selected}
          onChange={handleChange}
          sx={{ gap: 1, flexWrap: 'wrap', width: '100%' }}
        >
          {validPortions.map((portion) => (
            <FormControlLabel
              key={portion.id}
              value={portion.id}
              control={<StyledRadio size="small" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">
                    {portion.portionName}
                  </Typography>
                  {portion.priceModifier !== 0 && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        ml: 0.5,
                        color: portion.priceModifier > 0 
                          ? theme.success 
                          : theme.danger,
                      }}
                    >
                      {formatPriceModifier(portion.priceModifier)}
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                mr: 1,
                flex: '1 1 auto',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
          ))}
        </RadioGroup>
      </Box>
    );
  }

  // Full display for modals/detailed views
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
        Select Portion Size
      </Typography>
      <RadioGroup value={selected} onChange={handleChange}>
        {validPortions.map((portion) => (
          <FormControlLabel
            key={portion.id}
            value={portion.id}
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">
                    {portion.portionName}
                    {portion.isDefault && (
                      <Chip
                        label="Default"
                        size="small"
                        sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                      />
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {CURRENCY.symbol}{portion.effectivePrice.toFixed(2)}
                  </Typography>
                  {portion.priceModifier !== 0 && (
                    <PriceModifierChip
                      label={formatPriceModifier(portion.priceModifier)}
                      modifier={portion.priceModifier}
                      size="small"
                    />
                  )}
                </Box>
              </Box>
            }
            sx={{
              mb: 1,
              p: 1,
              borderRadius: 1,
              backgroundColor: selected === portion.id 
                ? theme.hover 
                : 'transparent',
              '&:hover': {
                backgroundColor: theme.hover,
              },
            }}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};

export default PortionSelector;